import { GridColDef } from '@mui/x-data-grid-pro'
import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetsFiltersBody } from 'src/components/BudgetsBoxWithFilters/BudgetsBoxWithFilters'
import DataGrid, {
  DataGridRowHeight,
  handleCellClick,
} from 'src/components/DataGrid/DataGrid'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import TeamAvatarsIcon from 'src/components/TeamAvatarsIcon/TeamAvatarsIcon'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrganizationInviteModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import { connectedPush } from 'src/navigation/Lnk'
import { budgetOverviewUrl, manageOrgInviteUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCreditCardsSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { InfiniteScrollProps } from 'src/types'
import styled, { css, CSSProp } from 'styled-components'

type BudgetsDataGridProps = {
  budgets: CreditCardListModel[]
  infiniteScrollProps?: InfiniteScrollProps<BudgetsFiltersBody>
  filters?: BudgetsFiltersBody
  clientHeight?: number
}

interface BudgetsDataGridRow {
  id: string
  budget: CreditCardListModel
  index: number
  canViewBudget: boolean
}

const StyledA = styled.a<{ budget?: CreditCardListModel; wrapText?: boolean }>`
  display: flex;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  overflow: hidden;
  ${({ wrapText }): CSSProp => {
    return wrapText
      ? css`
          white-space: nowrap;
        `
      : css``
  }}
  text-overflow: ellipsis;
  ${({ budget }): CSSProp => {
    return budget && budget?.isPending
      ? css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_dark,
            styleConstants.fontFamilies.regular,
          )};
        `
      : css``
  }}
`

const BudgetDataGrid: React.FC<BudgetsDataGridProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const users = useSelector(selectUserListModelsFromState)
  const currencyFormatter = useCurrencyFormatter()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const currentUser = useCurrentUser()
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const creditCardsSourceCount = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const { analytics } = useAnalyticsContext()

  const dispatch = useDispatch()
  let nonViewableCount = 0

  const rows: BudgetsDataGridRow[] = props.budgets.map((budget, i) => {
    const canViewBudget =
      budget.organizationId === currentUser?.organizationId ||
      budget?.ownerId === currentUser?.id
    if (!canViewBudget) {
      nonViewableCount++
    }
    return {
      id: budget?.id,
      budget: budget,
      index: i,
      canViewBudget: canViewBudget,
    }
  })

  const columns: Array<GridColDef<BudgetsDataGridRow> | undefined> = [
    {
      field: 'budgetName',
      headerName: translateSingleString(locale, 'Budget Name'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        const budget = params.row.budget
        const canViewBudget = params.row.canViewBudget
        let budgetAndInviteOrg: OrganizationsListResponse | undefined
        let correspondingInviteId
        Object.values(orgInvites).forEach((invite: OrganizationInviteModel) => {
          if (invite.organization.id === params.row.budget.organizationId) {
            budgetAndInviteOrg = invite.organization
            correspondingInviteId = invite.id
          }
        })
        return (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              box-sizing: border-box;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            <StyledA
              href={`${budgetOverviewUrl(params.row.budget.id)}`}
              css={css`
                ${canViewBudget
                  ? css`
                      align-items: center;
                    `
                  : css``}
                width: 100%;
                height: 100%;
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <img
                css={css`
                  height: 20px;
                `}
                src={budget.cardTypeImageSrc}
                alt={''}
              />
              <span
                css={css`
                  margin-left: 6px;
                  box-sizing: border-box;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {budget.truncatedDisplayName}
              </span>
            </StyledA>
            {!canViewBudget && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  bottom: -20px;
                  width: 100%;
                  border-radius: 10px;
                  border: 1px solid ${styleConstants.colors.red_dark};
                  background-color: ${styleConstants.colors.red_light};
                  margin-top: 20px;
                  padding: 12px 20px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  cursor: default;
                `}
                onClick={(e): void => {
                  e.preventDefault()
                }}
                onKeyPress={(e): void => {
                  e.preventDefault()
                }}
                role="button"
                tabIndex={0}
              >
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red_dark,
                      styleConstants.fontFamilies.medium,
                    )};
                    margin-bottom: 12px;
                  `}
                >
                  {translate('Action Required')}
                </span>
                {translate(
                  'To access the budget, you need to be a part of the org. ${cta}',
                  {
                    cta:
                      budgetAndInviteOrg !== undefined
                        ? translate(
                            'Accept your invite to join ${orgName} to access the budget and the permissions of a budget user.',
                            { orgName: budgetAndInviteOrg.name },
                          )
                        : translate(
                            'Please contact the Owner or Admin to get an invitation.',
                          ),
                  },
                )}
                {correspondingInviteId !== undefined && (
                  <HoverableLink
                    to={manageOrgInviteUrl(correspondingInviteId)}
                    ariaLabelSource={'Review the Invite'}
                    size={HoverableLinkSizes.Large}
                    underline
                    css={css`
                      width: fit-content;
                    `}
                  >
                    <span
                      css={css`
                        display: flex;
                        align-items: center;
                        ${getFontCss(
                          14,
                          themeConstants.colors.cta,
                          styleConstants.fontFamilies.regular,
                        )};
                        margin-top: 8px;
                      `}
                    >
                      {translate('Review the invite')}
                      <SvgArrowForward
                        css={css`
                          transform: scale(calc(16 / 24));
                        `}
                      />
                    </span>
                  </HoverableLink>
                )}
              </div>
            )}
          </div>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    creditCardsSourceCount > 0
      ? {
          field: 'budgetUser',
          headerName: translateSingleString(locale, 'Budget User'),
          sortable: false,
          flex: 0.5,
          minWidth: 200,
          maxWidth: 350,
          renderCell: (params): React.ReactElement => {
            const budget = params.row.budget
            return (
              <StyledA
                href={`${budgetOverviewUrl(params.row.budget.id)}`}
                css={css`
                  ${params.row.canViewBudget
                    ? css`
                        align-items: center;
                      `
                    : css``}
                  width: 100%;
                  height: 100%;
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {budget.budgetUsers !== undefined &&
                budget.budgetUsers.length > 1 ? (
                  <TeamAvatarsIcon users={budget.budgetUsers} showPill />
                ) : (
                  <UserAvatarAndName user={users[budget.userId]} />
                )}
              </StyledA>
            )
          },
          renderHeader: (params): React.ReactElement => {
            return (
              <div
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {params.colDef.headerName}
              </div>
            )
          },
        }
      : undefined,
    {
      field: 'budgetCreator',
      headerName: translateSingleString(locale, 'Account Owner'),
      sortable: false,
      minWidth: 200,
      maxWidth: 350,
      renderCell: (params): React.ReactElement => {
        const budget = params.row.budget
        return (
          <StyledA
            href={`${budgetOverviewUrl(params.row.budget.id)}`}
            css={css`
              ${params.row.canViewBudget
                ? css`
                    align-items: center;
                  `
                : css``}
              width: 100%;
              height: 100%;
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <UserAvatarAndName user={users[budget.ownerId]} />
          </StyledA>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'exposure',
      headerName: translateSingleString(locale, 'Exposure'),
      sortable: false,
      minWidth: 150,
      maxWidth: 250,
      renderCell: (params): React.ReactElement => {
        const budget = params.row.budget
        return (
          <StyledA
            href={`${budgetOverviewUrl(params.row.budget.id)}`}
            css={css`
              ${params.row.canViewBudget
                ? css`
                    align-items: center;
                  `
                : css``}
              width: 100%;
              height: 100%;
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <span
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {budget.unspentAmountLabel}
            </span>
          </StyledA>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'remaining',
      headerName: translateSingleString(locale, 'Remaining'),
      sortable: false,
      minWidth: 150,
      maxWidth: 250,
      renderCell: (params): React.ReactElement => {
        const budget = params.row.budget
        return (
          <StyledA
            href={`${budgetOverviewUrl(params.row.budget.id)}`}
            css={css`
              ${params.row.canViewBudget
                ? css`
                    align-items: center;
                  `
                : css``}
              width: 100%;
              height: 100%;
              ${params.row.canViewBudget &&
              css`
                ${getFontCss(
                  14,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              `}
              ${params.row.canViewBudget &&
              (budget.isInactive || budget.isClosed) &&
              css`
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            `}
          >
            {params.row.canViewBudget
              ? budget.unissuedAmountLabel
              : currencyFormatter({
                  amount: 0,
                  currency: budget.currency,
                  maximumFractionDigits: 0,
                }).replace('0', '********')}
          </StyledA>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'chevron',
      headerName: translateSingleString(locale, 'Chevron'),
      disableColumnMenu: true,
      resizable: false,
      sortable: false,
      hideable: false,
      headerClassName: 'hideRightSeparator',
      width: 20,
      renderCell: (params): React.ReactElement => {
        return (
          <StyledA
            href={`${budgetOverviewUrl(params.row.budget.id)}`}
            css={css`
              ${params.row.canViewBudget
                ? css`
                    align-items: center;
                  `
                : css``}
              width: 100%;
              height: 100%;
            `}
          >
            <SvgChevronRight
              css={css`
                transform: scale(0.75);
              `}
              data-testid={'chevron'}
            />
          </StyledA>
        )
      },
      renderHeader: (): React.ReactElement => {
        return <></>
      },
    },
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter(
        (column): column is GridColDef<BudgetsDataGridRow> =>
          column !== undefined,
      ),
    [columns],
  )

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columnsSafe}
      rowHeight={nonViewableCount > 0 ? 160 : DataGridRowHeight.Comfy}
      infiniteScrollProps={props.infiniteScrollProps}
      filtersBody={props.filters || {}}
      onColumnVisibilityModelChange={(): void => {
        analytics?.track('budgets - column selector', {
          screen: calculatedBackgroundLocation.pathname.replace(
            /\/cc_[a-zA-Z0-9]+/,
            '',
          ),
        })
      }}
      alwaysVisibleColumns={['budgetName']}
      disableColumnFilter
      onCellClick={handleCellClick}
      onRowClick={(row): void => {
        const budget = row.row.budget
        dispatch(
          connectedPush({
            pathname: budgetOverviewUrl(budget?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track('budget - select budget - item press', {
          budgetSelected: budget.id,
        })
      }}
    />
  )
}

export default BudgetDataGrid
